import { useControllableState } from '@anchorage/hooks';
import { Input } from '@latitude/input';
import { Content, Root, Trigger } from '@latitude/popover';
import { Calendar } from 'components/Calendar';
import { ErrorMessage } from 'components/ErrorMessage';
import { DatepickerContext } from 'hooks/useDatepickerContext';
import { useDatepickerInput } from 'hooks/useDatepickerInput';
import { useId, useMemo, useState } from 'react';
import type { ModeValue } from 'utils/dateUtils';
import {
  formatValue,
  getDefaultFormat,
  getDefaultMaxDate,
  getDefaultMinDate,
  isValidDate,
  isValueEqual,
  sanitizeDate,
} from 'utils/dateUtils';
import { classesFor, sizes } from 'utils/designSystemTokens';
import { selectDayHandler } from 'utils/eventUtils';

import type { DatepickerProps } from './types';

export const Datepicker = (props: DatepickerProps) => {
  const {
    mode,
    value: valueProp,
    open: openProp,
    setOpen: setOpenProp,
    onOpenChange,
    format: formatProp,
    minDate: minDateProp,
    maxDate: maxDateProp,
    disabled = false,
    readOnly = false,
    required = false,
    onClear,
    onBlur,
    placeholder = '',
    children,
    id,
    'data-testid': testId,
    'aria-label': ariaLabel,
  } = props;

  // Passing the `value` key at all makes the parent the owner, even while it
  // is undefined: an empty form field is still a controlled one.
  const isControlled = 'value' in props;
  const format = formatProp || getDefaultFormat(mode);
  const [uncontrolledValue, setUncontrolledValue] =
    useState<ModeValue['value']>();
  const rawValue = isControlled ? valueProp : uncontrolledValue;
  const selection = useMemo(
    () => sanitizeDate(mode, rawValue),
    [mode, rawValue],
  );

  // The value the parent last passed. If the parent stops passing `value`,
  // the picker keeps it instead of going blank.
  const [lastControlled, setLastControlled] = useState(
    isControlled ? selection : undefined,
  );
  if (isControlled) {
    if (!lastControlled || !isValueEqual(lastControlled, selection)) {
      setLastControlled(selection);
    }
  } else if (lastControlled) {
    setLastControlled(undefined);
    setUncontrolledValue(lastControlled.value);
  }

  const [open, setOpen] = useControllableState(false, openProp, setOpenProp);
  const [changingMonth, setChangingMonth] = useState(false);
  const [changingYear, setChangingYear] = useState(false);
  const [yearPage, setYearPage] = useState(0);

  const minDate = getDefaultMinDate(minDateProp);
  const maxDate = getDefaultMaxDate(maxDateProp);

  const commit = (next: ModeValue) => {
    if (
      next.mode !== 'range' &&
      next.value !== undefined &&
      !isValidDate(next.value)
    ) {
      return;
    }
    if (!isControlled) {
      setUncontrolledValue(next.value);
    }
    const formattedValue = formatValue(next, format);
    // `next` is built from `props.mode`, so both checks agree; testing both
    // pairs each `onChange` with its own payload type.
    if (props.mode === 'range' && next.mode === 'range') {
      props.onChange?.({ value: next.value, formattedValue });
    } else if (props.mode !== 'range' && next.mode !== 'range') {
      props.onChange?.({ value: next.value, formattedValue });
    }
  };

  const isLocked = disabled || readOnly;
  const handleOpenChange = (nextOpen: boolean) => {
    // Radix's trigger calls this directly too, and a click on the input's
    // padding reaches it even while the input is disabled, so the lock is
    // enforced here rather than only in the input's click handler.
    if (nextOpen && isLocked) {
      return;
    }
    if (!nextOpen) {
      // The calendar reopens on the month view, not on the grid left open.
      setChangingMonth(false);
      setChangingYear(false);
      setYearPage(0);
    }
    setOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  const { inputProps, errorMessage, syncText } = useDatepickerInput({
    selection,
    format,
    minDate,
    maxDate,
    commit,
    openCalendar: () => handleOpenChange(true),
    disabled,
    readOnly,
    required,
    placeholder,
    id,
    testId,
    ariaLabel,
    onClear,
    onBlur,
  });
  const hasError = errorMessage !== undefined;
  const errorMessageId = useId();

  // Rewrites the text even when the pick equals the committed value, so it
  // also replaces partly typed text.
  const commitFromCalendar = (next: ModeValue) => {
    syncText(next);
    commit(next);
  };

  const handleDayClick = (day: Date) =>
    selectDayHandler(day, {
      selection,
      onSelect: commitFromCalendar,
      onClose: () => handleOpenChange(false),
    });

  // A time that changes nothing, such as Cancel right after opening, still
  // rewrites the text, so typed text and its pending commit are dropped.
  const handleTimeChange = (time: Date | undefined) => {
    if (selection.mode === 'range') {
      return;
    }
    const next: ModeValue = { mode: selection.mode, value: time };
    if (isValueEqual(next, selection)) {
      syncText(next);
      return;
    }
    commitFromCalendar(next);
  };

  const defaultMonth =
    selection.mode === 'range'
      ? (selection.value?.from ?? selection.value?.to)
      : selection.value;

  return (
    <DatepickerContext.Provider
      value={{
        selection,
        onDayClick: handleDayClick,
        onTimeChange: handleTimeChange,
        onOpenChange: handleOpenChange,
        changingMonth,
        setChangingMonth,
        changingYear,
        setChangingYear,
        yearPage,
        setYearPage,
      }}
    >
      <div className="relative">
        <Root open={!isLocked && open} onOpenChange={handleOpenChange}>
          {/* asChild: a trigger button can't hold the input's own buttons.
              Radix still sets type="button" on the child, which breaks the
              placeholder (radix-ui/primitives#1461), so type is reset. */}
          <Trigger asChild type={undefined}>
            {children || (
              <span>
                <Input
                  {...inputProps}
                  aria-describedby={hasError ? errorMessageId : undefined}
                  aria-invalid={hasError}
                />
              </span>
            )}
          </Trigger>
          <Content
            align="start"
            data-testid="datepicker-popover"
            className={`${classesFor(sizes.popover)} ${sizes.popoverHeight[mode]}`}
          >
            <Calendar
              mode={mode}
              selected={selection.value}
              onDayClick={handleDayClick}
              captionLayout="dropdown-buttons"
              numberOfMonths={1}
              fromDate={minDate}
              toDate={maxDate}
              defaultMonth={defaultMonth ?? new Date()}
              initialFocus={false}
            />
          </Content>
        </Root>

        <ErrorMessage
          id={errorMessageId}
          show={hasError}
          message={errorMessage}
        />
      </div>
    </DatepickerContext.Provider>
  );
};
